import React from 'react';
import { User, IdCard, UserSquare2, Users2, Phone, Info, MapPin, Home, Users } from 'lucide-react';
import { formatDisplayDate, computeAge } from '../lib/dateFormat';

// DOB with age inline, e.g. "25-Sep-1980 (45 yrs)". Falls back to stored member.age.
const formatMemberDob = (member) => {
  const dob = formatDisplayDate(member.dob);
  const age = computeAge(member.dob) ?? (member.age ? Number(member.age) : null);
  if (dob && age != null && !Number.isNaN(age)) return `${dob} (${age} yrs)`;
  return dob;
};

// Bilingual section metadata shared across all family forms & views.
// Each section carries a distinct jewel-tone identity (icon chip + heading + left accent)
// so the colours propagate consistently to registration, directory, edit & admin surfaces.
// NOTE: class strings are written literally so Tailwind's JIT scanner picks them up.
export const FAMILY_SECTIONS = {
  basic:        { icon: User,        en: 'Basic Information',    hi: 'मूल जानकारी',   chip: 'bg-blue-100 text-blue-700',       accent: 'border-l-blue-600',    heading: 'text-blue-800' },
  personal:     { icon: IdCard,      en: 'Personal Information', hi: 'व्यक्तिगत जानकारी', chip: 'bg-amber-100 text-amber-700',     accent: 'border-l-amber-500',   heading: 'text-amber-800' },
  paternal:     { icon: UserSquare2, en: 'Paternal Information', hi: 'पितृ पक्ष',      chip: 'bg-indigo-100 text-indigo-700',   accent: 'border-l-indigo-600',  heading: 'text-indigo-800' },
  maternal:     { icon: Users2,      en: 'Maternal Information', hi: 'मातृ पक्ष',      chip: 'bg-rose-100 text-rose-700',       accent: 'border-l-rose-600',    heading: 'text-rose-800' },
  contact:      { icon: Phone,       en: 'Contact Information',  hi: 'संपर्क जानकारी',  chip: 'bg-teal-100 text-teal-700',       accent: 'border-l-teal-600',    heading: 'text-teal-800' },
  other:        { icon: Info,        en: 'Other Information',    hi: 'अन्य जानकारी',    chip: 'bg-slate-100 text-slate-700',     accent: 'border-l-slate-500',   heading: 'text-slate-800' },
  currentPlace: { icon: MapPin,      en: 'Current Place',        hi: 'वर्तमान निवास',   chip: 'bg-emerald-100 text-emerald-700', accent: 'border-l-emerald-600', heading: 'text-emerald-800' },
  nativePlace:  { icon: Home,        en: 'Native Place',         hi: 'मूल निवास',      chip: 'bg-orange-100 text-orange-700',   accent: 'border-l-orange-600',  heading: 'text-orange-800' },
  familyInfo:   { icon: Users,       en: 'Family Information',   hi: 'परिवार जानकारी',  chip: 'bg-purple-100 text-purple-700',   accent: 'border-l-purple-600',  heading: 'text-purple-800' },
};

// Elevated bilingual section header: icon inside a coloured rounded chip + bold heading.
const SectionHeader = ({ meta, size = 'md' }) => {
  const Icon = meta.icon;
  const chipSize = size === 'sm' ? 'w-7 h-7' : 'w-9 h-9';
  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-5 h-5';
  const titleSize = size === 'sm' ? 'text-xs' : 'text-sm';
  return (
    <div className={`flex items-center gap-2.5 ${size === 'sm' ? 'mb-2' : 'mb-3'}`}>
      {Icon && (
        <span className={`inline-flex items-center justify-center ${chipSize} rounded-lg ${meta.chip || 'bg-primary/10 text-primary'} shrink-0`}>
          <Icon className={iconSize} />
        </span>
      )}
      <h4 className={`${titleSize} font-bold uppercase tracking-wide ${meta.heading || 'text-primary'}`}>
        {meta.en}
        {meta.hi && <span className="text-gray-400 font-normal normal-case ml-1.5">/ {meta.hi}</span>}
      </h4>
    </div>
  );
};

// Editable-form section wrapper: coloured icon chip + bilingual heading + responsive grid
export const FamilyFormSection = ({ sectionKey, columns = 3, children, className = '' }) => {
  const meta = FAMILY_SECTIONS[sectionKey] || {};
  const colClass = { 1: 'md:grid-cols-1', 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4' }[columns] || 'md:grid-cols-3';
  return (
    <div className={`mb-5 ${className}`}>
      <SectionHeader meta={meta} size="md" />
      <div className={`grid grid-cols-1 ${colClass} gap-4 pl-6 border-l-4 ${meta.accent || 'border-l-primary/30'}`}>
        {children}
      </div>
    </div>
  );
};

const ViewField = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="font-medium break-words">{value || '-'}</p>
  </div>
);

const ViewSection = ({ sectionKey, columns = 4, children }) => {
  const meta = FAMILY_SECTIONS[sectionKey] || {};
  const colClass = { 1: 'md:grid-cols-1', 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4' }[columns] || 'md:grid-cols-4';
  return (
    <div>
      <SectionHeader meta={meta} size="sm" />
      <div className={`grid grid-cols-2 ${colClass} gap-4 text-sm pl-6 border-l-4 ${meta.accent || 'border-l-primary/30'}`}>
        {children}
      </div>
    </div>
  );
};

// Read-only sectioned display of a single family member. Used by admin & portal view modals.
export const MemberSections = ({ member }) => (
  <div className="space-y-5">
    <ViewSection sectionKey="basic">
      <ViewField label="Name / नाम" value={member.name} />
      <ViewField label="Relation with Head / मुखिया से संबंध" value={member.relationWithHead} />
      <ViewField label="Gender / लिंग" value={member.gender} />
    </ViewSection>

    <ViewSection sectionKey="personal">
      <ViewField label="Marital Status / वैवाहिक स्थिति" value={member.maritalStatus} />
      <ViewField label="DOB / जन्म तिथि" value={formatMemberDob(member)} />
      <ViewField label="Education / शिक्षा" value={member.education} />
      <ViewField label="Occupation / व्यवसाय" value={member.occupation} />
      <ViewField label="Blood Group / रक्त समूह" value={member.bloodGroup} />
      <ViewField label="Spouse Name / जीवनसाथी" value={member.spouseName} />
    </ViewSection>

    <ViewSection sectionKey="paternal">
      <ViewField label="Father's Name / पिता का नाम" value={member.fatherName} />
      <ViewField label="Father's Nukh / पिता का नुख" value={member.nukh} />
      <ViewField label="Mother's Name / माता का नाम" value={member.motherName} />
      <ViewField label="Mother's Nukh / माता का नुख" value={member.motherNukh} />
    </ViewSection>

    <ViewSection sectionKey="maternal">
      <ViewField label="Maternal's Name / नाना-मामा का नाम" value={member.maternalName} />
      <ViewField label="Maternal's Nukh / मातृ नुख" value={member.maternalNukh} />
      <ViewField label="Maternal's Place / मातृ स्थान" value={member.maternalPlace} />
    </ViewSection>

    <ViewSection sectionKey="contact">
      <ViewField label="Email Address / ईमेल" value={member.emailId} />
      <ViewField label="Mobile No / मोबाइल" value={member.mobileNo} />
      <ViewField label="Social Media / सोशल मीडिया" value={member.socialMediaId} />
    </ViewSection>

    {member.other && (
      <ViewSection sectionKey="other">
        <div className="md:col-span-4">
          <p className="font-medium whitespace-pre-wrap">{member.other}</p>
        </div>
      </ViewSection>
    )}
  </div>
);


// Read-only sectioned display of a family's top section (Current Place, Native Place,
// Family Information). Used by admin & portal view modals. Head email shown only when showHeadEmail.
export const FamilyTopView = ({ family, showHeadEmail = false }) => (
  <div className="space-y-5">
    <ViewSection sectionKey="currentPlace" columns={3}>
      <ViewField label="Address / पता" value={family.currentPlace} />
      <ViewField label="City / Village / शहर / गाँव" value={family.currentCity} />
      <ViewField label="District / जिला" value={family.currentDistrict || family.district} />
      <ViewField label="State / राज्य" value={family.currentState || family.state} />
      <ViewField label="Country / देश" value={family.currentCountry || family.country} />
    </ViewSection>

    <ViewSection sectionKey="nativePlace" columns={3}>
      <ViewField label="Address / पता" value={family.originPlace} />
      <ViewField label="City / Village / शहर / गाँव" value={family.originCity} />
      <ViewField label="District / जिला" value={family.originDistrict} />
      <ViewField label="State / राज्य" value={family.originState} />
      <ViewField label="Country / देश" value={family.originCountry} />
    </ViewSection>

    <ViewSection sectionKey="familyInfo" columns={3}>
      <ViewField label="Family Nukh / परिवार नुख" value={family.familyNukh} />
      {showHeadEmail && (
        <ViewField label="Family Head Login Email / मुखिया लॉगिन ईमेल" value={family.headEmail || family.email} />
      )}
    </ViewSection>
  </div>
);
